import React from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { ColorMapType } from '@types';
import { Notification } from '@models/Notifications/NotificationModel';
import { NotificationTypeEnum } from '@api';
import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { Touchable } from '../common/Touchable';

const iconColorMap: ColorMapType<NotificationTypeEnum> = {
  [NotificationTypeEnum.NewVideo]: '$primary300',
  [NotificationTypeEnum.SubDay]: '$error300',
  [NotificationTypeEnum.SubBefore]: '$waring300',
  [NotificationTypeEnum.NewTrack]: '$info300',
  [NotificationTypeEnum.NewMention]: '$info300',
  [NotificationTypeEnum.NewFeedLike]: '$info300',
  [NotificationTypeEnum.NewMessage]: '$info300',
};

const iconNamesMap: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.NewVideo]: 'tick-square-bold',
  [NotificationTypeEnum.SubDay]: 'close-square-bold',
  [NotificationTypeEnum.SubBefore]: 'danger-bold',
  [NotificationTypeEnum.NewTrack]: 'info-bold',
  [NotificationTypeEnum.NewMention]: 'info-bold',
  [NotificationTypeEnum.NewFeedLike]: 'info-bold',
  [NotificationTypeEnum.NewMessage]: 'info-bold',
};

const Container = styled(Touchable)({
  width: '100%',
  flexDirection: 'row',
  borderRadius: 2,
});

const ContentContainer = styled(View)({
  flex: 1,
  mr: '$4',
});

const Message = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
});

const Time = styled(BaseText)({
  color: '$text400',
  variant: 'text.small',
  mt: '$2',
});

const TypeIcon = styled(Icon)({
  mr: '$4',
});

const Dot = styled(View)({
  size: 8,
  borderRadius: 4,
  bg: '$primary300',
});

export interface INotificationItemProps {
  item: Notification;
  onPress(item: Notification): void;
}

export const NotificationItem = observer(
  ({ item, onPress }: INotificationItemProps) => {
    return (
      <Container onPress={() => onPress(item)}>
        <TypeIcon
          name={iconNamesMap[item.type]}
          color={iconColorMap[item.type]}
          size={20}
        />
        <ContentContainer>
          <Message numberOfLines={4}>{item.message}</Message>
          <Time>{item.formattedSendTime}</Time>
        </ContentContainer>
        {item.isNew && <Dot />}
      </Container>
    );
  },
);
